import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { KigSettings, KIG_SETTINGS } from '../settings/kig-settings';
import { VERSION_INFO, VersionInfo } from '../main';
import { MsalBroadcastService } from '@azure/msal-angular';
import { UserInformationService } from '@corteva-research/ngx-components-core';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { DistributedTracingModes } from '@microsoft/applicationinsights-common';
import { DataShareService } from '@services/dataShare.service';
import { ThemeService, Theme } from '@services/theme.service';
import { UserService } from '@services/user.service';


@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  public globalSwitch = false;
  public mobileSwitch = false;
  public mode = 'Show All';
  public uiEnvLabel = 'oo';
  public appName = 'Kig';
  public currentTheme: Theme = 'light';

  public hideHeader: boolean = false;
  subscription: Subscription;
  private lastSiteLoadStamped = false;

  title = 'Keep It Green';
  copyright = `Corteva Agriscience ${new Date().getFullYear()}`;
  releaseName: string;
  releaseUrl: string;

  apiBuildNumber: string;
  public uiBlocked$: Observable<boolean>;


  constructor(@Inject(KIG_SETTINGS) private settings: KigSettings, private router: Router,
              @Inject(VERSION_INFO) public versionInfo: VersionInfo, private msalBroadcastService: MsalBroadcastService,
              private userInfoService: UserInformationService, private dataShareService: DataShareService,
              private themeService: ThemeService, private userService: UserService) {

    // TEMPORARILY DISABLED - Application Insights was blocking HTTP connections
    /*
    const angularPlugin = new AngularPlugin();
    const appInsights = new ApplicationInsights({
      config: {
        connectionString: settings.appInsightsConnectionString,
        extensions: [angularPlugin],
        extensionConfig: {
          [angularPlugin.identifier]: { router: this.router }
        },
        disableCookiesUsage: false,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        correlationHeaderExcludedDomains: [],
        distributedTracingMode: DistributedTracingModes.AI,
      }
    });
    */

    const angularPlugin = new AngularPlugin();
    const appInsights = new ApplicationInsights({
      config: {
        connectionString: settings.appInsightsConnectionString,
        extensions: [angularPlugin],
        extensionConfig: {
          [angularPlugin.identifier]: { router: this.router }
        },
        
        // KEY SETTINGS TO PREVENT CONNECTION BLOCKING:
        maxAjaxCallsPerView: 3,           // Limit to 3 concurrent telemetry requests
        samplingPercentage: 50,           // Only send 50% of telemetry (less load)
        disableAjaxTracking: false,       // Keep this false
        maxBatchSizeInBytes: 10000,       // Smaller batches
        maxBatchInterval: 5000,           // Send every 5 seconds instead of immediately
        
        // DISABLE IN DEV:
        disableTelemetry: settings.envName === 'Local',  // ← Add this line
        
        // Existing settings:
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        distributedTracingMode: DistributedTracingModes.AI,
      }
    });


    this.uiEnvLabel = this.settings.envName;

    if (this.uiEnvLabel == 'Stage') {
      this.appName = 'DOTs';
    }
    else {
      this.appName = 'KIG';
    }

    this.userInfoService.authStatus$.subscribe(status => {
      if (status == 'authenticated'){
        const userInfo = this.userInfoService.getUserInformation();

        if (userInfo == null) {
          return;
        }

        const name = userInfo.displayName.replace(' ', '_');
        const userId = userInfo.uniqueId.replace(' ', '_');

        appInsights.clearAuthenticatedUserContext();
        appInsights.setAuthenticatedUserContext(userId, name, true);

        appInsights.trackTrace({ message: `Authenticated User` });

        // Stamp the user's last site-load date once per full browser load (not on SPA route changes).
        if (!this.lastSiteLoadStamped) {
          this.lastSiteLoadStamped = true;
          this.userService.getCurrentUserEmailUsingAzureAD().subscribe(r => {
            const email = r['value'];
            if (email) {
              this.userService.updateLastSiteLoadDate(email).subscribe();
            }
          });
        }
      }
    });

    this.subscription = this.dataShareService.headerState$.subscribe(headerState => {
      this.hideHeader = headerState;
    });

    appInsights.loadAppInsights();
  }

  ngOnInit() {
    this.themeService.currentTheme$.subscribe(theme => {
      this.currentTheme = theme;
    });

    if (this.uiEnvLabel == 'Stage') {
      this.globalSwitch = true;
    }
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  ngOnDestroy() {
  }

  changeHeaderVisibility(hideHeader: boolean) {
    this.hideHeader = hideHeader;
  }

  login() {
    console.log('Login Button Pressed');
  }

  setMobileState(switchState: boolean) {
    this.mobileSwitch = switchState;
    if (this.mobileSwitch) {
      this.router.navigate(['/events']);
    }
  }

}

